import React, { useState } from 'react';
import { Plus, SearchIcon } from '../../icons';
import SearchCard from './SearchCard';
import SearchModal from './SearchModal';
import ErrorDisplay from '../common/ErrorDisplay';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';

const SearchesContainer = ({
  trackedSearches,
  isLoading,
  error,
  onAddSearch,
  onDeleteSearch,
  onScanSearch,
  scanningSearchId,
  newlyFoundItems,
  formatDate,
  onTrackItem,
  addToast,
  onToggleOpportunityTracking,
  getOpportunityStatsForSearch,
  onNavigateToOpportunities,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchUrl, setSearchUrl] = useState('');

  const handleSubmitSearch = async (detectedPlatform) => {
    try {
      await onAddSearch({
        name: searchName,
        searchUrl: searchUrl,
        platform: detectedPlatform,
      });
      setShowAddModal(false);
      setSearchName('');
      setSearchUrl('');
      if (addToast) {
        addToast('✅ Search query added successfully!', 'success');
      }
    } catch (error) {
      console.error('Error adding search:', error);
      if (addToast) {
        addToast(`❌ Failed to add search: ${error.message}`, 'error');
      } else {
        alert(error.message || 'Failed to add search');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading tracked searches">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-card border border-line rounded-card p-4 animate-pulse space-y-3">
            <div className="h-5 w-48 bg-card-2 rounded" />
            <div className="h-3 w-3/4 bg-card-2 rounded" />
            <div className="h-9 w-full bg-card-2 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Display */}
      <ErrorDisplay error={error} />

      {/* Desktop toolbar */}
      <div className="hidden lg:flex justify-end">
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4" /> Track search
        </Button>
      </div>

      {/* Searches List */}
      {trackedSearches.length === 0 ? (
        <EmptyState
          icon={SearchIcon}
          title="No search queries tracked yet"
          description="Save a Bazaraki, BuySell or DOM search page and scan it for new listings whenever you like."
          action={
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4" /> Track search
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {trackedSearches.map((search) => (
            <SearchCard
              key={search.id}
              search={search}
              onScan={onScanSearch}
              onDelete={onDeleteSearch}
              isScanning={scanningSearchId === search.id}
              newlyFoundItems={newlyFoundItems[search.id] || []}
              formatDate={formatDate}
              onTrackItem={onTrackItem}
              onToggleOpportunityTracking={onToggleOpportunityTracking}
              opportunityStats={getOpportunityStatsForSearch ? getOpportunityStatsForSearch(search.id) : null}
              onNavigateToOpportunities={onNavigateToOpportunities}
            />
          ))}
        </div>
      )}

      {/* Mobile FAB */}
      <button
        type="button"
        onClick={() => setShowAddModal(true)}
        aria-label="Track search"
        className="lg:hidden fixed right-4 bottom-[calc(64px+env(safe-area-inset-bottom)+1rem)] z-30 w-14 h-14 rounded-full bg-accent text-[#062A24] shadow-[0_10px_30px_-8px_rgba(20,211,176,0.8)] flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Add Search Modal */}
      <SearchModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSubmitSearch}
        searchName={searchName}
        setSearchName={setSearchName}
        searchUrl={searchUrl}
        setSearchUrl={setSearchUrl}
      />
    </div>
  );
};

export default SearchesContainer;
